import type { TechlineAssetEntity } from '@packages/domain/techline/assets';

import { colors } from '@packages/core/styles';
import { noop } from '@packages/core/utils';
import { AlertBar, Icon, Typography } from '@packages/ui/shared';
import { useCallback, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { createUseStyles } from 'react-jss';

import { CASE_ATTACHMENT_DROPZONE_HINT, isSupportedCaseAttachmentFile } from '../case-attachment-file-types';
import { Attachment } from './attachment/attachment';

const DEFAULT_DROP_ZONE_LABEL = 'Click to upload or drag and drop';

/** Props for the AttachmentList component. */
export type AttachmentListProps = {
    /** Whether to show the upload dropzone. Defaults to true. */
    allowUpload?: boolean;

    /** The unified asset list (completed + in-progress). */
    assetData: TechlineAssetEntity[];

    /** Whether the attachment list is in read-only mode. Hides edit and delete actions on individual attachments. */
    isReadOnly?: boolean;

    /** Main dropzone label. Defaults to "Click to upload or drag and drop". */
    dropzoneLabel?: string;

    /** Dropzone hint text describing accepted formats. Also shown in the rejection error message. */
    dropzoneHint?: string;

    /** Callback when the user clicks delete on an attachment. */
    onDelete: (assetId: string) => void;

    /** Callback when the user clicks download on an attachment. */
    onDownload: (assetId: string) => void;

    /** Callback when the user clicks edit on an attachment (images only). */
    onEdit?: (assetId: string) => void;

    /** Callback when files are dropped or selected in the dropzone. */
    onUpload: (files: File[]) => void;

    /** Callback when the user clicks view on an attachment (images only). */
    onView?: (assetId: string) => void;

    /** Optional file validation that overrides the default extension-based check. Used for SEPF-only uploads. */
    validateFile?: (file: File) => boolean;
};

/**
 * Render a list of attachment items with an optional upload dropzone.
 * Purely presentational; all mutation callbacks are forwarded to the parent.
 *
 * @param props - the asset data, upload config, and callbacks
 * @returns the rendered attachment list
 */
export function AttachmentList({
    allowUpload = true,
    assetData,
    dropzoneLabel = DEFAULT_DROP_ZONE_LABEL,
    dropzoneHint = CASE_ATTACHMENT_DROPZONE_HINT,
    isReadOnly = false,
    onDelete,
    onDownload,
    onEdit,
    onUpload,
    onView,
    validateFile,
}: AttachmentListProps) {
    // State
    const [rejectedFileNames, setRejectedFileNames] = useState<string[]>([]);
    const dismissTimerRef = useRef<ReturnType<typeof setTimeout>>();

    // Derived state

    /** When read-only, the dropzone is always hidden regardless of the allowUpload prop. */
    const isUploadAllowed = allowUpload && !isReadOnly;

    // Forwarded callback aliases
    const handleDelete = onDelete;
    const handleDownload = onDownload;
    const handleEdit = onEdit ?? noop;
    const handleView = onView ?? noop;

    // Hooks
    const classes = useStyles();

    // Handlers

    /**
     * Handle file drop events. Validates file types and delegates
     * valid files to the onUpload callback. Shows an inline warning
     * for any rejected files.
     *
     * @param files - the dropped or selected files
     */
    const handleDrop = useCallback(
        (files: File[]) => {
            const rejected: string[] = [];
            const validFiles = files.filter((file) => {
                const isValid = validateFile ? validateFile(file) : isSupportedCaseAttachmentFile(file);
                if (!isValid) {
                    rejected.push(file.name);
                    return false;
                }
                return true;
            });

            // Show inline warning for rejected files, auto-dismiss after 10 seconds
            if (rejected.length) {
                setRejectedFileNames(rejected);
                clearTimeout(dismissTimerRef.current);
                dismissTimerRef.current = setTimeout(() => setRejectedFileNames([]), 10000);
            }

            if (validFiles.length) {
                onUpload(validFiles);
            }
        },
        [onUpload, validateFile]
    );

    /**
     * Handle dismissing the rejected files warning.
     */
    const handleRejectedAlertClose = useCallback(() => {
        setRejectedFileNames([]);
        clearTimeout(dismissTimerRef.current);
    }, []);

    const { getInputProps, getRootProps } = useDropzone({ onDrop: handleDrop });

    return (
        <>
            {isUploadAllowed && (
                <div {...getRootProps()} className={classes.dropZone}>
                    <input {...getInputProps()} />
                    <Icon color="blueOne" name="export" size={24} style={uploadIconStyle} />
                    <Typography color="textDarkPrimary" style={dropTextMainStyle} variant="caption">
                        {dropzoneLabel}
                    </Typography>
                    <Typography style={dropTextHintStyle} variant="caption">
                        {dropzoneHint}
                    </Typography>
                </div>
            )}

            {rejectedFileNames.length > 0 && (
                <div style={rejectedAlertStyle}>
                    <AlertBar error onClose={handleRejectedAlertClose} show={true}>
                        <Typography>
                            Unsupported file format: {rejectedFileNames.join(', ')}.<br />
                            Accepted formats: {dropzoneHint}
                        </Typography>
                    </AlertBar>
                </div>
            )}

            {assetData.map((asset) => (
                <Attachment
                    asset={asset}
                    isReadOnly={isReadOnly}
                    key={asset.id}
                    onDelete={handleDelete}
                    onDownload={handleDownload}
                    onEdit={handleEdit}
                    onView={handleView}
                />
            ))}
        </>
    );
}

// Style constants

const rejectedAlertStyle = { marginBottom: 12 };

const dropTextHintStyle = { fontSize: 13, color: colors.textDarkTertiary };

const dropTextMainStyle = { fontSize: 15, fontWeight: 400 as const, marginBottom: 8 };

const uploadIconStyle = { marginBottom: 16 };

const useStyles = createUseStyles({
    dropZone: {
        alignItems: 'center',
        backgroundColor: colors.white,
        border: `1px solid ${colors.grayFive}`,
        borderRadius: 4,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        height: 144,
        justifyContent: 'center',
        marginBottom: 8,
        padding: 16,
        textAlign: 'center',
    },
});
